import React from "react"
import {FormProvider, useForm} from "react-hook-form"
import {Trans, useTranslation} from "react-i18next"
import {toast} from "react-toastify"
import {zodResolver} from "@hookform/resolvers/zod"
import {format} from "date-fns"
import {z} from "zod"

import Button, {ButtonForm} from "../../../components/Button"
import {TextareaField} from "../../../components/fields/Textarea"
import {DateField} from "../../../components/formElements/DateField"
import {DropdownField} from "../../../components/formElements/Dropdown/Dropdown"
import {VariantModal} from "../../../components/Modal"
import {useSalesAssistantTasksPartialUpdateMutation} from "../../../queries/salesAssistant"
import requestError from "../../../services/requestError"
import {
  ASalesAssistantNextSteps,
  ASalesAssistantTask,
  ASalesAssistantTaskTypes,
} from "../../../services/types.generated"
import {commonTransComponents, enumToOptions, enumTranslKey} from "../../../utils/i18n"
import {validateNativeEnum} from "../../../utils/validation"

const SALES_ASSISTANT_TASK_TYPE_TO_ENUM_KEY: Record<ASalesAssistantTaskTypes, string> = {
  [ASalesAssistantTaskTypes.SalesAssistantTasksEmail]: "email",
  [ASalesAssistantTaskTypes.SalesAssistantTasksCall]: "call",
  [ASalesAssistantTaskTypes.SalesAssistantTasksMeeting]: "meeting",
  [ASalesAssistantTaskTypes.SalesAssistantTasksOther]: "other",
}

const validationSchema = z.object({
  next_step: validateNativeEnum(ASalesAssistantNextSteps),
  next_step_date: z.date(),
  notes: z.string().optional(),
})

export const MarkAsDoneButton: React.FC<{plannedTask: ASalesAssistantTask; organization: string}> = ({
  plannedTask,
  organization,
}) => {
  const {t} = useTranslation()
  const nextStepOptions = enumToOptions("SalesAssistantNextSteps", true)

  const [isModalVisible, setIsModalVisible] = React.useState(false)

  const salesAssistantTasksPartialUpdateMutation = useSalesAssistantTasksPartialUpdateMutation()

  const handleSubmit = React.useCallback(
    async (values: z.infer<typeof validationSchema>) => {
      try {
        await salesAssistantTasksPartialUpdateMutation.mutateAsync({
          id: plannedTask.id,
          next_step: values.next_step,
          next_step_date: format(values.next_step_date, "yyyy-MM-dd"),
          notes: values.notes,
        })
        setIsModalVisible(false)
        toast.success(t("SalesAssistant_Deals_MarkAsDoneConfirmModal_SuccessToast"))
      } catch (e) {
        requestError(e)
      }
    },
    [plannedTask, salesAssistantTasksPartialUpdateMutation, t]
  )

  const methods = useForm<z.infer<typeof validationSchema>>({
    mode: "onSubmit",
    resolver: zodResolver(validationSchema),
    defaultValues: {
      next_step: ASalesAssistantNextSteps.Email,
      next_step_date: new Date(),
      notes: "",
    },
  })

  const nextStep = methods.watch("next_step")
  const type = t(enumTranslKey("SalesAssistantTaskTypes", SALES_ASSISTANT_TASK_TYPE_TO_ENUM_KEY[plannedTask.type]))
  const shouldShowDatepicker =
    nextStep &&
    ![ASalesAssistantNextSteps.Won, ASalesAssistantNextSteps.Lost, ASalesAssistantNextSteps.Nothing].includes(nextStep)

  return (
    <>
      <Button fullWidth={false} onClick={() => setIsModalVisible(true)} color={"blue"} size={"sm"}>
        {t("SalesAssistant_Deals_Table_MarkAsDone")}
      </Button>
      <VariantModal
        noIcon={true}
        size={"lg"}
        title={t("SalesAssistant_Deals_MarkAsDoneConfirmModal_Title")}
        isOpen={isModalVisible}
        onClose={() => setIsModalVisible(false)}
      >
        <div className={"flex flex-col gap-2"}>
          <div className={"text-gray-900"}>
            <Trans
              i18nKey={"SalesAssistant_Deals_MarkAsDoneConfirmModal_Text"}
              values={{organization, type}}
              components={commonTransComponents}
            />
          </div>
          <div className={"mt-7 mb-3 h-px self-stretch bg-indigo-100"}></div>
          <FormProvider {...methods}>
            <form onSubmit={methods.handleSubmit(handleSubmit)}>
              <div className={"flex items-center justify-between"}>
                <div>
                  <div className={"font-bold text-gray-900"}>
                    {t("SalesAssistant_Deals_MarkAsDoneConfirmModal_NextActivity")}
                  </div>
                  <div className={"text-zinc-500"}>
                    {t("SalesAssistant_Deals_MarkAsDoneConfirmModal_NextActivityText")}
                  </div>
                </div>
                <DropdownField
                  name={"next_step"}
                  label={""}
                  variant={"small"}
                  options={nextStepOptions}
                  listWidth={"list-auto"}
                />
              </div>
              {shouldShowDatepicker && (
                <>
                  <div className={"my-3 h-px self-stretch bg-indigo-100"}></div>
                  <div className={"flex items-center justify-between"}>
                    <div>
                      <div className={"font-bold text-gray-900"}>
                        {t("SalesAssistant_Deals_MarkAsDoneConfirmModal_Date")}
                      </div>
                      <div className={"text-zinc-500"}>{t("SalesAssistant_Deals_MarkAsDoneConfirmModal_DateText")}</div>
                    </div>
                    <div>
                      <DateField name={"next_step_date"} label={""} />
                    </div>
                  </div>
                </>
              )}
              <div className={"my-3 mb-7 h-px self-stretch bg-indigo-100"}></div>
              <TextareaField
                rows={7}
                name={"notes"}
                label={t("SalesAssistant_Deals_MarkAsDoneConfirmModal_Note")}
                labelClassName={"text-base font-bold text-gray-900"}
              />
              <div className={"flex justify-end gap-2"}>
                <Button onClick={() => setIsModalVisible(false)} variant={"outlined"} color={"gray"}>
                  {t("SalesAssistant_Deals_MarkAsDoneConfirmModal_Cancel")}
                </Button>
                <ButtonForm>{t("SalesAssistant_Deals_MarkAsDoneConfirmModal_Confirm")}</ButtonForm>
              </div>
            </form>
          </FormProvider>
        </div>
      </VariantModal>
    </>
  )
}
